import { useState, useEffect, useRef, useCallback } from "react";
import ApexCharts from "apexcharts";
import SkeletonLoader from "../skeletonLoader.jsx";
import axios from "axios";
import ProgressBar from "../progressBar.jsx";
import { useTranslation } from "react-i18next";

function AttemptHistoryChart({ data }) {

    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        const isModuleData = Array.isArray(data) && data.length && (data[0].moduleTitle || data[0].moduleId) && (typeof data[0].progress === 'number');
        const isPassFailData = Array.isArray(data) && data.length && (typeof data[0].passed === 'number' || typeof data[0].failed === 'number');

        const fallbackTimeseries = [
            { date: 'Mon', completedCount: 342, inProgressCount: 300 },
            { date: 'Tue', completedCount: 442, inProgressCount: 400 },
            { date: 'Wed', completedCount: 311, inProgressCount: 280 },
            { date: 'Thu', completedCount: 421, inProgressCount: 390 },
            { date: 'Fri', completedCount: 212, inProgressCount: 200 }
        ];

        const fallbackPassFail = [
            { date: '2025-11-30', passed: 0, failed: 0 }
        ];

        // prefer given data, otherwise pick a sensible fallback based on shape
        let d;
        if (isPassFailData) d = (Array.isArray(data) && data.length) ? data : fallbackPassFail;
        else d = (Array.isArray(data) && data.length) ? data : fallbackTimeseries;

        let options = {};

        if (isPassFailData) {
            const categories = d.map((i) => i.date || '');
            const series = [
                { name: 'Passed', data: d.map((i) => i.passed || 0) },
                { name: 'Failed', data: d.map((i) => i.failed || 0) }
            ];
            options = {
                chart: { type: 'bar', stacked: true, height: 250, toolbar: { show: false } },
                series,
                plotOptions: { bar: { borderRadius: 6, horizontal: false } },
                colors: ['#10b981', '#ef4444'],
                dataLabels: { enabled: false },
                grid: { show: false },
                xaxis: { categories, labels: { rotate: -20, style: { fontSize: '12px' } } },
                yaxis: { labels: { formatter: v => `${Math.round(v)}` } },
                tooltip: { y: { formatter: val => `${val}` }, theme: 'light' }
            };
        } else if (isModuleData) {
            const categories = d.map((i) => i.moduleTitle || i.moduleId);
            const series = [{ name: 'Progress', data: d.map((i) => i.progress || 0) }];
            options = {
                chart: { type: 'bar', height: 250, toolbar: { show: false } },
                series,
                plotOptions: { bar: { borderRadius: 8, horizontal: false } },
                colors: ['#B03982'],
                dataLabels: { enabled: false },
                grid: { show: false },
                xaxis: { categories, labels: { rotate: -45, style: { fontSize: '12px' } } },
                yaxis: { labels: { formatter: v => `${Math.round(v)}%` }, max: 100 },
                tooltip: { y: { formatter: val => `${val}%` }, theme: 'light' }
            };
        } else {
            const completedSeries = d.map((i) => i.completedCount || 0);
            const inProgressSeries = d.map((i) => i.inProgressCount || 0);
            const categories = d.map((i) => i.date || '');
            options = {
                chart: { type: 'area', height: 250, toolbar: { show: false }, zoom: { enabled: false } },
                series: [{ name: 'Completed', data: completedSeries }, { name: 'In Progress', data: inProgressSeries }],
                stroke: { curve: 'smooth', width: 2 },
                fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0 } },
                colors: ['#2563eb', '#22c55e'],
                dataLabels: { enabled: false },
                grid: { show: false },
                xaxis: { categories, labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
                yaxis: { labels: { show: false } },
                legend: { show: false },
                tooltip: { theme: 'light' }
            };
        }

        chartInstance.current?.destroy();
        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();
        return () => chartInstance.current?.destroy();
    }, [data]);

    // simple helpers for header info
    const isModuleDataForHeader = Array.isArray(data) && data.length && (data[0].moduleTitle || data[0].moduleId) && (typeof data[0].progress === 'number');

    let headerMain = '—';
    let headerSub = '';
    if (isModuleDataForHeader) {
        const avg = Math.round((data || []).reduce((s, i) => s + (i.progress || 0), 0) / (data.length || 1));
        headerMain = `${avg}%`;
        headerSub = `${data.length} modules`;
    } else {
        const totalCompleted = (Array.isArray(data) ? data.reduce((s, i) => s + (i.completedCount || 0), 0) : 0);
        headerMain = totalCompleted.toLocaleString();
        headerSub = 'Users this week';
    }

    // const toggleDesigner = () => {
    //     chartInstance.current?.toggleSeries("In Progress");
    // };

    return (
        <div className="max-w-full w-full bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:p-6">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h5 className="text-2xl font-semibold text-gray-900">{headerMain}</h5>
                    <p className="text-sm text-gray-500">{headerSub}</p>
                </div>

                {!isModuleDataForHeader ? (
                    <div className="flex items-center px-2.5 py-0.5 text-sm font-medium text-green-600">
                        <svg
                            className="w-5 h-5 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 6v13m0-13 4 4m-4-4-4 4"
                            />
                        </svg>
                        12%
                    </div>
                ) : null}
            </div>

            {/* Chart */}
            <div ref={chartRef} className="mt-4" />

            {/* Footer */}
            {/* {!isModuleDataForHeader ? (
                <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between items-center">
                    <button
                        onClick={toggleDesigner}
                        className="text-sm font-medium text-gray-500 hover:text-gray-900 inline-flex items-center"
                    >
                        Last 7 days
                        <svg
                            className="w-4 h-4 ml-1.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="m19 9-7 7-7-7"
                            />
                        </svg>
                    </button>
                    <a
                        href="#"
                        className="inline-flex items-center text-blue-600 hover:bg-gray-100 font-medium rounded-lg text-sm px-3 py-2"
                    >
                        Users Report
                        <svg
                            className="w-4 h-4 ml-1.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 12H5m14 0-4 4m4-4-4-4"
                            />
                        </svg>
                    </a>
                </div>
            ) : null} */}
        </div>
    );
}

const CircularProgress = ({ assigned = 0, completed = 0, size = 96, strokeWidth = 12 }) => {

    // keep `total` exactly as provided (can be 0). Avoid forcing to 1.
    const total = assigned;

    const center = size / 2;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    // Guard all divisions against total === 0
    const completedClamped = total > 0 ? Math.min(Math.max(completed, 0), total) : 0;
    const remaining = total > 0 ? Math.max(total - completedClamped, 0) : 0;

    const completedLength = total > 0 ? (completedClamped / total) * circumference : 0;
    const remainingLength = total > 0 ? (remaining / total) * circumference : 0;
    const completedPercent = total > 0 ? Math.round((completedClamped / total) * 100) : 0;

    return (
        <div className="flex items-center gap-4">
            <svg width={size} height={size} className="transform rotate-[-90deg]">
                <circle cx={center} cy={center} r={radius} stroke="#e5e7eb" strokeWidth={strokeWidth} fill="none" />
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    stroke="#10b981"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={`${completedLength} ${circumference - completedLength}`}
                    style={{ transition: 'stroke-dasharray 0.8s ease, stroke 0.3s ease' }}
                />
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    stroke="#f59e0b"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={`${remainingLength} ${circumference - remainingLength}`}
                    style={{ strokeDashoffset: `${circumference - completedLength}`, transition: 'stroke-dasharray 0.8s ease, stroke-dashoffset 0.8s ease' }}
                />
                <g transform={`rotate(${total > 0 ? (completedClamped / total) * 360 : 0}, ${center}, ${center})`}>
                    <circle cx={center} cy={center - radius} r={strokeWidth / 2} fill="#10b981" className="animate-blinkPulse" />
                </g>
            </svg>
            <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Completed</p>
                <p className="text-xl font-bold text-[#10b981]">{completedClamped} <span className="text-sm font-medium text-gray-500">/ {total}</span></p>
                <p className="text-sm mt-1 text-gray-500">{completedPercent}%</p>
            </div>
        </div>
    );
};

function LineChart({ dir }) {

    const { t } = useTranslation();
    const base_url = process.env.REACT_APP_LMS_APP_BASE_URL;
    const token = localStorage.getItem("token");

    const [activeTab, setActiveTab] = useState("overview");
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // const radius = 45;
    // const circumference = 2 * Math.PI * radius;
    // const progress = Math.min(Math.max(20, 0), 100);
    // const strokeDashoffset = circumference - (progress / 100) * circumference;

    const chartRefs = {
        overview: useRef(null),
        onboarding: useRef(null),
        onJob: useRef(null),
        quiz: useRef(null),
    };

    const chartInstances = useRef({});

    // simple loading simulation to show skeletons
    useEffect(() => {
        const t = setTimeout(() => setLoading(false), 700);
        return () => clearTimeout(t);
    }, []);

    // Initialize charts
    useEffect(() => {
        if (!dashboardData) return;

        // snapshot the current charts container
        const charts = chartInstances.current;

        // destroy existing charts before re-creating
        Object.values(charts).forEach(chart => chart?.destroy());
        chartInstances.current = {};
        // Overview Donut Chart
        const overviewOptions = {
            chart: { type: "donut", height: 320, toolbar: { show: false } },
            series: [dashboardData?.learningOverview?.completed, dashboardData?.learningOverview?.inProgress, dashboardData?.learningOverview?.notStarted, dashboardData?.learningOverview?.assigned],
            labels: ["Completed", "In Progress", "Not Started", "Assigned"],
            colors: ["#3b82f6", "#f59e0b", "#9ca3af", "#10b981"],
            plotOptions: {
                pie: {
                    donut: {
                        size: "65%",
                        labels: {
                            show: true,
                            name: {
                                show: true,
                                fontSize: "14px",
                                fontFamily: "Segoe UI, sans-serif",
                                color: "#6b7280"
                            },
                            value: {
                                show: true,
                                fontSize: "20px",
                                fontFamily: "Segoe UI, sans-serif",
                                fontWeight: 700,
                                color: "#1f2937"
                            },
                            total: {
                                show: true,
                                label: "Total",
                                fontSize: "14px",
                                color: "#6b7280"
                            }
                        }
                    }
                }
            },
            dataLabels: { enabled: false },
            legend: { position: "bottom", fontSize: "13px", fontFamily: "Segoe UI, sans-serif" },
            tooltip: { theme: "light" }
        };

        if (chartRefs.overview.current) {
            chartInstances.current.overview = new ApexCharts(chartRefs.overview.current, overviewOptions);
            chartInstances.current.overview.render();
        }

        // Onboarding Progress Chart
        const onboardingOptions = {
            chart: { type: "bar", height: 300, toolbar: { show: false } },
            series: [{
                name: "Progress %",
                data: dashboardData?.onboarding?.courses.map(c => c.progress)
            }],
            xaxis: { categories: dashboardData?.onboarding?.courses.map(c => c.name.substring(0, 15)) },
            colors: ["#3b82f6"],
            plotOptions: { bar: { borderRadius: 4, horizontal: false } },
            tooltip: { theme: "light" }
        };

        if (chartRefs.onboarding.current) {
            chartInstances.current.onboarding = new ApexCharts(chartRefs.onboarding.current, onboardingOptions);
            chartInstances.current.onboarding.render();
        }

        // On-Job Learning Chart
        const onJobOptions = {
            chart: { type: "line", height: 300, toolbar: { show: false } },
            series: [{
                name: "Completion %",
                data: dashboardData?.onJob?.courses.map(c => c.progress)
            }],
            xaxis: { categories: dashboardData?.onJob?.courses.map(c => c.name) },
            colors: ["#10b981"],
            stroke: { curve: "smooth", width: 3 },
            tooltip: { theme: "light" }
        };

        if (chartRefs.onJob.current) {
            chartInstances.current.onJob = new ApexCharts(chartRefs.onJob.current, onJobOptions);
            chartInstances.current.onJob.render();
        }

        // Quiz Performance Chart
        const quizOptions = {
            chart: { type: "bar", height: 300, toolbar: { show: false } },
            series: [
                {
                    name: "Passed",
                    data: [dashboardData?.quizzes?.passed]
                },
                {
                    name: "Failed",
                    data: [dashboardData?.quizzes?.failed]
                }
            ],
            xaxis: { categories: ["Quiz Results"] },
            colors: ["#10b981", "#ef4444"],
            plotOptions: { bar: { borderRadius: 4 } },
            tooltip: { theme: "light" }
        };

        if (chartRefs.quiz.current) {
            chartInstances.current.quiz = new ApexCharts(chartRefs.quiz.current, quizOptions);
            chartInstances.current.quiz.render();
        }

        return () => {
            Object.values(chartInstances.current).forEach(chart => chart?.destroy());
        };
    }, [dashboardData, chartRefs.onJob, chartRefs.onboarding, chartRefs.overview, chartRefs.quiz]);

    // icon renderer (simple inline SVGs)
    const renderIcon = (name) => {
        const common = { className: 'w-7 h-7', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 };
        switch (name) {
            case 'timer':
                return (
                    <svg {...common} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2 2" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h10M6 8a6 6 0 1112 0 6 6 0 01-12 0z" />
                    </svg>
                );
            case 'chart':
                return (
                    <svg {...common} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 13v6M12 8v11M17 4v15" />
                    </svg>
                );
            case 'check':
                return (
                    <svg {...common} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                );
            case 'trophy':
                return (
                    <svg {...common} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3h8v4" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 8a3 3 0 003 3h8a3 3 0 003-3" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a6 6 0 006 0" />
                    </svg>
                );
            default:
                return null;
        }
    };

    // Reusable Components
    const KPICard = ({ title, count = 0 }) => {
        if (loading) {
            return (
                <SkeletonLoader count={1} className="min-h-[112px] w-full rounded-lg" style={{ borderRadius: '0.75rem' }} />
            );
        }
        return (
            <div className="rounded-lg p-4 transform transition-transform duration-200 hover:scale-102 bg-[#B03982] hover:bg-[#733C86] text-white shadow-sm border border-transparent">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-md font-medium opacity-95">{title}</p>
                        <p className="text-2xl font-semibold mt-1">{count}<span className="text-md font-normal opacity-90"></span></p>
                    </div>
                    <div className="text-3xl bg-white/10 rounded-full p-2">
                        {renderIcon('icon')}
                    </div>
                </div>
            </div>
        );
    };

    // const ProgressBar = ({ label, value, color = "blue" }) => {
    //     const colorClass = {
    //         blue: "bg-blue-500",
    //         green: "bg-green-500",
    //         red: "bg-red-500",
    //         yellow: "bg-yellow-500"
    //     }[color];
    //     return (
    //         <div className="mb-4">
    //             <div className="flex justify-between items-center mb-2">
    //                 <span className="text-md font-medium text-gray-700 dark:text-gray-300">{label}</span>
    //                 <span className="text-md font-bold text-[#B03982]">{value}%</span>
    //             </div>
    //             <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-sm">
    //                 <div className={`${colorClass} h-3 rounded-full transition-all duration-500 ease-out`} style={{ width: `${value}%` }}></div>
    //             </div>
    //         </div>
    //     );
    // };

    // const DeadlineTimeline = ({ deadline, priority }) => {
    //     const priorityColor = {
    //         high: "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200",
    //         medium: "bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200",
    //         low: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200"
    //     }[priority];

    //     return (
    //         <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
    //             <div className="flex-1">
    //                 <p className="text-md font-medium text-gray-900 dark:text-white">{deadline.course}</p>
    //                 <p className="text-md text-gray-500 dark:text-gray-400">Due: {deadline.dueDate} ({deadline.daysLeft} days)</p>
    //             </div>
    //             <div className={`px-3 py-1 rounded-full text-md font-semibold ${priorityColor}`}>
    //                 {priority.charAt(0).toUpperCase() + priority.slice(1)}
    //             </div>
    //         </div>
    //     );
    // };

    // const OverdueAlert = ({ course, dueDate }) => (
    //     <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900 rounded-lg border border-red-200 dark:border-red-700">
    //         <span className="text-red-600 dark:text-red-200 text-lg">⚠️</span>
    //         <div>
    //             <p className="text-md font-medium text-red-900 dark:text-red-100">{course}</p>
    //             <p className="text-md text-red-700 dark:text-red-300">Overdue since {dueDate}</p>
    //         </div>
    //     </div>
    // );

    // Tab Components

    console.log(dashboardData?.learningOverview)

    const LearningOverviewTab = () => (

        <div className="space-y-6">

            <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Performance Indicators</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <KPICard title="Assigned Modules" count={dashboardData?.learningOverview?.assignedModulesCount} />
                    <KPICard title="Completed Modules" count={dashboardData?.learningOverview?.completedModulesCount} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-8">
                        <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Overall Learning Progress
                    </h3>
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Overall Progress</p>
                            <p className="text-sm font-bold text-[#B03982]">{dashboardData?.learningOverview?.overallProgress}%</p>
                        </div>
                        <ProgressBar progress={dashboardData?.learningOverview?.overallProgress} showProgress={false} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-8">
                        <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Assigned and Completion
                    </h3>
                    <div className="mb-8">
                        <div className="flex items-center gap-6">
                            <CircularProgress assigned={dashboardData?.learningOverview?.assignedModulesCount || 0} completed={dashboardData?.learningOverview?.completedModulesCount || 0} size={150} strokeWidth={12} />
                        </div>
                    </div>
                </div>
            </div>

            {/* <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-8">
                    <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Overall Learning Progress
                </h3>
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Overall Completion</p>
                        <p className="text-sm font-bold text-[#B03982]">{dashboardData?.learningOverview?.overallProgress}%</p>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-sm">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out" style={{ width: `${dashboardData?.learningOverview?.overallProgress}%` }}></div>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-5 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-2 border-blue-200 dark:border-blue-700 hover:shadow-lg hover:border-blue-400 transition-all">
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-10 h-10 rounded-lg bg-blue-200 dark:bg-blue-900/50 flex items-center justify-center">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">{dashboardData?.learningOverview?.completed}</p>
                        <p className="text-md font-semibold text-blue-700 dark:text-blue-300">Completed</p>
                    </div>
                    <div className="p-5 rounded-xl bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:to-yellow-800/30 border-2 border-yellow-200 dark:border-yellow-700 hover:shadow-lg hover:border-yellow-400 transition-all">
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-10 h-10 rounded-lg bg-yellow-200 dark:bg-yellow-900/50 flex items-center justify-center">
                                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mb-1">{dashboardData?.learningOverview?.inProgress}</p>
                        <p className="text-md font-semibold text-yellow-700 dark:text-yellow-300">In Progress</p>
                    </div>
                    <div className="p-5 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 border-2 border-gray-300 dark:border-gray-600 hover:shadow-lg hover:border-gray-400 transition-all">
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-10 h-10 rounded-lg bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                <svg className="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-1">{dashboardData?.learningOverview?.notStarted}</p>
                        <p className="text-md font-semibold text-gray-600 dark:text-gray-400">Not Started</p>
                    </div>
                    <div className="p-5 rounded-xl bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 border-2 border-green-200 dark:border-green-700 hover:shadow-lg hover:border-green-400 transition-all">
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-10 h-10 rounded-lg bg-green-200 dark:bg-green-900/50 flex items-center justify-center">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1m2-1v2.5M4 7l2 1m-2-1l2-1m-2 1v2.5" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">{dashboardData?.learningOverview?.assigned}</p>
                        <p className="text-md font-semibold text-green-700 dark:text-green-300">Assigned</p>
                    </div>
                </div>
            </div> */}

        </div>
    );

    const OnboardingTab = () => (
        <div className="space-y-6">

            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <KPICard title="Days Left To Complete" count={dashboardData?.onboardingSection?.daysLeftToComplete || 0} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-8">
                        <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Overall Learning Progress
                    </h3>
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Overall Progress</p>
                            <p className="text-sm font-bold text-[#B03982]">{dashboardData?.onboardingSection?.onboardingProgress}%</p>
                        </div>
                        <ProgressBar progress={dashboardData?.onboardingSection?.onboardingProgress} showProgress={false} />
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-8">
                    <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Completed and Remaining
                </h3>
                <div className="mb-8">
                    <div className="flex items-center gap-6">
                        <CircularProgress assigned={dashboardData?.onboardingSection?.contentRemaining || 0} completed={dashboardData?.onboardingSection?.contentCompleted || 0} size={150} strokeWidth={12} />
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-8">
                    <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Quiz Progress
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <KPICard title="Quiz Attempts Count" count={dashboardData?.quizAndAssessments?.onboarding?.totalQuizAttempts || 0} />
                    <KPICard title="Latest Quiz Score" count={dashboardData?.quizAndAssessments?.onboarding?.latestQuizScore || 0} />
                    <KPICard title="Passed Quizzes" count={dashboardData?.quizAndAssessments?.onboarding?.passedQuizzes || 0} />
                    <KPICard title="Failed Quizzes" count={dashboardData?.quizAndAssessments?.onboarding?.failedQuizzes || 0} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-1 mt-10">
                    <AttemptHistoryChart data={dashboardData?.quizAndAssessments?.onboarding?.passVsFailHistory} />
                </div>
            </div>

        </div>
    );

    const OnJobTab = () => (
        <div className="space-y-6">

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Total" count={dashboardData?.onJobSection?.totalOnJobModules} />
                <KPICard title="In Progress" count={dashboardData?.onJobSection?.inProgressOnJobModules} />
                <KPICard title="Completed" count={dashboardData?.onJobSection?.completedOnJobModules} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                    <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Progress Over Time
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
                    <AttemptHistoryChart data={dashboardData?.onJobSection?.progressOverTime} />
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                    <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Module Completion
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
                    <AttemptHistoryChart data={dashboardData?.onJobSection?.moduleCompletionStatus} />
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-8">
                    <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Quiz Progress
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <KPICard title="Quiz Attempts Count" count={dashboardData?.quizAndAssessments['on-job-training']?.totalQuizAttempts || 0} />
                    <KPICard title="Latest Quiz Score" count={dashboardData?.quizAndAssessments['on-job-training']?.latestQuizScore || 0} />
                    <KPICard title="Passed Quizzes" count={dashboardData?.quizAndAssessments['on-job-training']?.passedQuizzes || 0} />
                    <KPICard title="Failed Quizzes" count={dashboardData?.quizAndAssessments['on-job-training']?.failedQuizzes || 0} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-1 mt-10">
                    <AttemptHistoryChart data={dashboardData?.quizAndAssessments['on-job-training']?.passVsFailHistory} />
                </div>
            </div>

        </div>
    );

    // const QuizTab = () => (
    //     <div className="space-y-6">
    //         <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
    //             <KPICard label="Total Attempts" value={dashboardData.quizzes.totalAttempts} unit="attempts" trend="This Period" icon="chart" />
    //             <KPICard label="Passed" value={dashboardData.quizzes.passed} unit="quizzes" trend="Success!" icon="check" />
    //             <KPICard label="Failed" value={dashboardData.quizzes.failed} unit="quizzes" trend="Need Review" icon="chart" />
    //             <KPICard label="Avg Score" value={dashboardData.quizzes.avgScore} unit="%" trend="Overall" icon="chart" />
    //         </div>

    //         <div ref={chartRefs.quiz} />

    //         <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
    //             <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
    //                 <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Recent Quiz Scores
    //             </h3>
    //             <div className="space-y-4">
    //                 {dashboardData.quizzes.recentScores.map((score, idx) => (
    //                     <div key={idx} className="flex items-center justify-between p-5 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 border border-gray-200 dark:border-gray-600 hover:shadow-md hover:border-[#B03982]/30 transition-all">
    //                         <div>
    //                             <p className="text-md font-semibold text-gray-900 dark:text-white">{score.quiz}</p>
    //                             <p className="text-md text-gray-500 dark:text-gray-400 mt-1">Performance</p>
    //                         </div>
    //                         <div className="flex items-center gap-4">
    //                             <div className="text-right">
    //                                 <div className="text-2xl font-bold text-[#B03982]">{score.score}%</div>
    //                                 <div className="text-md text-gray-500 dark:text-gray-400">Score</div>
    //                             </div>
    //                             <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center">
    //                                 <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    //                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    //                                 </svg>
    //                             </div>
    //                         </div>
    //                     </div>
    //                 ))}
    //             </div>
    //         </div>
    //     </div>
    // );

    const CertificatesTab = () => (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />Your Certificates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboardData?.certificatesAndAchievements?.certificates.map((cert, idx) => (
                    <div key={idx} className="relative p-4 rounded-xl bg-gradient-to-br from-[#B03982]/10 to-[#733C86]/10 border-2 border-[#B03982]/30 hover:border-[#B03982] hover:shadow-lg transition-all group overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#B03982]/20 to-transparent rounded-full -mr-10 -mt-10"></div>
                        <div className="relative z-10">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#B03982] to-[#733C86] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                <svg
                                    className={"w-9 h-9"}
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 92.35 122.88"
                                >
                                    <defs>
                                        <style>{`
          .cls-1 {
            fill-rule: evenodd;
          }
        `}</style>
                                    </defs>

                                    <title>certified</title>

                                    <path
                                        fill="white"
                                        className="cls-1"
                                        d="M46.18,0a9.26,9.26,0,0,1,5.61,1.76C54,3.16,56.45,5.91,59.5,7.65c4.28,2.45,12.22-.93,16.29,5.11,2.37,3.52,2.48,6.28,2.66,9a15.8,15.8,0,0,0,3.72,9.63c5,6.6,6,11,3.45,15.57-1.75,3.11-5.44,4.85-6.29,6.82-1.82,4.2.19,7.37-2.3,12.27a13.05,13.05,0,0,1-7.93,6.78c-3,1-6-.43-8.39.58C56.5,75.19,53.39,79.3,50,80.34a13,13,0,0,1-7.73,0c-3.35-1-6.45-5.15-10.66-6.92-2.4-1-5.4.39-8.39-.58a13,13,0,0,1-7.94-6.78c-2.49-4.9-.48-8.07-2.3-12.27-.85-2-4.54-3.71-6.29-6.82C4.16,42.39,5.2,38,10.19,31.4a15.92,15.92,0,0,0,3.72-9.63c.17-2.73.28-5.49,2.66-9,4.06-6,12-2.66,16.29-5.11,3-1.74,5.51-4.49,7.7-5.88A9.29,9.29,0,0,1,46.18,0ZM89,113.07,77.41,111l-5.73,10.25c-4.16,5.15-6.8-3.31-8-6.26L52.57,94c2.57-.89,5.67-3.47,8.85-6.35,6.35.13,12.27-1,16.62-6.51l12.82,24.75L92,108.22c.87,3.09.41,5.13-3,4.85Zm-85.57,0L15,111l5.73,10.25c4.15,5.15,6.79-3.31,8-6.26L39.78,94c-2.57-.89-5.66-3.47-8.85-6.35-6.35.13-12.27-1-16.62-6.51L1.5,105.85.38,108.22c-.87,3.09-.41,5.13,3,4.85Zm36.13-76.8,4.72,4.45,9.49-9.64c.93-.95,1.52-1.71,2.68-.52l3.76,3.84c1.23,1.22,1.17,1.94,0,3.08L46.38,51c-2.45,2.41-2,2.56-4.51.09l-8.68-8.64a1.09,1.09,0,0,1,.1-1.69l4.36-4.52c.66-.68,1.19-.64,1.87,0Zm6.54-19.34A24.16,24.16,0,1,1,21.91,41.09,24.16,24.16,0,0,1,46.06,16.93Z"
                                    />
                                </svg>
                            </div>
                            <p className="text-md font-bold text-gray-900 dark:text-white mb-2">{cert.courseTitle}</p>
                            <p className="text-md text-gray-600 dark:text-gray-400 mb-4">Earned on {new Date(cert.completionDate).toLocaleDateString()}</p>
                            <div className="flex justify-end items-center gap-2">
                                {/* <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-md font-semibold text-green-600 dark:text-green-400">Verified</span>
                                </div> */}
                                <div>
                                    <span className="text-sm font-medium text-green-600 dark:text-green-400">{cert?.certificateId}</span>
                                </div>
                            </div>
                            <button
                                className="w-full h-10 bg-gradient-to-b from-[#B03982] to-[#733C86] text-white font-semibold rounded-full hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 mt-4"
                                onClick={() => window.open(cert.s3Url, "_blank")}
                            >
                                {t("Download Certificate")}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // const DeadlinesTab = () => (
    //     <div className="space-y-6">
    //         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
    //             <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
    //                 <span className="inline-block mr-3 w-1 h-6 rounded bg-[#B03982] align-middle" />⚠️ Overdue Courses
    //             </h3>
    //             {dashboardData.deadlines.overdue.length > 0 ? (
    //                 <div className="space-y-3">
    //                     {dashboardData.deadlines.overdue.map((deadline, idx) => (
    //                         <OverdueAlert key={idx} course={deadline.course} dueDate={deadline.dueDate} />
    //                     ))}
    //                 </div>
    //             ) : (
    //                 <p className="text-md text-gray-600 dark:text-gray-400">No overdue courses! 🎉</p>
    //             )}
    //         </div>

    //         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
    //             <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
    //                 <span className="inline-block mr-3 w-1 h-6 rounded bg-[#B03982] align-middle" />📅 Upcoming Deadlines
    //             </h3>
    //             <div className="space-y-3">
    //                 {dashboardData.deadlines.upcomingDeadlines.map((deadline, idx) => (
    //                     <DeadlineTimeline key={idx} deadline={deadline} priority={deadline.priority} />
    //                 ))}
    //             </div>
    //         </div>
    //     </div>
    // );

    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${base_url}/dashboard/analytics`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const data = response.data.data;

            // ensure we have sensible fallbacks for onJobSection arrays
            const defaultProgressOverTime = [
                { date: 'Mon', completedCount: 342, inProgressCount: 300 },
                { date: 'Tue', completedCount: 442, inProgressCount: 400 },
                { date: 'Wed', completedCount: 311, inProgressCount: 280 },
                { date: 'Thu', completedCount: 421, inProgressCount: 390 },
                { date: 'Fri', completedCount: 212, inProgressCount: 200 }
            ];

            const defaultModuleCompletionStatus = [
                {
                    moduleId: "",
                    moduleTitle: "",
                    courseName: "",
                    progress: 0,
                    status: "",
                    daysLeft: null
                }
            ];

            if (!data.onJobSection) data.onJobSection = {};
            if (!Array.isArray(data.onJobSection.progressOverTime) || data.onJobSection.progressOverTime.length === 0) {
                data.onJobSection.progressOverTime = defaultProgressOverTime;
            }
            if (!Array.isArray(data.onJobSection.moduleCompletionStatus) || data.onJobSection.moduleCompletionStatus.length === 0) {
                data.onJobSection.moduleCompletionStatus = defaultModuleCompletionStatus;
            }

            setDashboardData(data);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    }, [base_url, token]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Add this useEffect for sidebar state management
    useEffect(() => {
        const updateSidebarState = () => {
            const sidebarState = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-open');
            setSidebarOpen(sidebarState === '1');
        };

        updateSidebarState();

        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        const observer = new MutationObserver(updateSidebarState);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Calculate responsive padding based on sidebar state and screen size
    const getContentPadding = () => {
        if (isMobile) {
            return "";
        }
        return sidebarOpen
            ? (dir === 'rtl' ? 'pr-64' : 'pl-64')
            : (dir === 'rtl' ? 'pr-20' : 'pl-20');
    };

    return (
        <div className={`transition-all duration-300 ${getContentPadding()} p-4`}>
            {/* Header */}
            <div className="px-4 md:px-6 pb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Learning Dashboard</h2>
                <p className="text-md text-gray-600 dark:text-gray-400 mt-1">Track your learning progress and achievements</p>
            </div>
            {/* Tabs */}
            <div className="flex flex-wrap gap-0 px-4 md:px-6">
                {[
                    { id: "overview", label: "Overview" },
                    { id: "onboarding", label: "Onboarding" },
                    { id: "onJob", label: "OnJob Learning" },
                    // { id: "quiz", label: "Quizzes" },
                    { id: "certificates", label: "Certificates" },
                    // { id: "deadlines", label: "Deadlines" }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 text-md font-medium rounded-full transition-all duration-200 mr-3 whitespace-nowrap ${activeTab === tab.id ? 'bg-[#B03982] text-white shadow-md' : 'text-gray-600 dark:text-gray-400 hover:bg-[#B03982]/10'}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            {/* Content */}
            <div className="p-4 md:p-6 relative">
                <style>{`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: translateY(8px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    .animate-fadeIn {
                        animation: fadeIn 0.4s ease-in-out;
                    }

                    @keyframes blinkPulse {
                        0% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.35); opacity: 0.25; }
                        100% { transform: scale(1); opacity: 1; }
                    }
                    /* Make sure SVG elements use the correct transform box and origin so scaling works */
                    .animate-blinkPulse {
                        transform-box: fill-box;
                        transform-origin: 50% 50%;
                        animation: blinkPulse 1.2s infinite ease-in-out;
                        will-change: transform, opacity;
                    }
                `}</style>
                <div className="animate-fadeIn">
                    {activeTab === "overview" && <LearningOverviewTab />}
                    {activeTab === "onboarding" && <OnboardingTab />}
                    {activeTab === "onJob" && <OnJobTab />}
                    {/* {activeTab === "quiz" && <QuizTab />} */}
                    {activeTab === "certificates" && <CertificatesTab />}
                    {/* {activeTab === "deadlines" && <DeadlinesTab />} */}
                </div>
            </div>
        </div>
    );

}

export default LineChart;